<template>
  <div class="guide-slider">
    <div class="steps-container">
      <div
        v-for="(image, i) in stepImages"
        :key="i"
        :class="`steps ${animate ? 'fade' : ''}`"
        ref="steps"
      >
        <div class="title" v-if="guide">{{ guide.attributes.title }}</div>
        <div class="page">{{ i + 1 }} / {{ stepImages.length }}</div>
        <div class="image" :style="`background-image: url(${image})`" />
        <div class="caption">{{ stepCaptions[i] }}</div>
      </div>
      <div v-if="!stepImages.length">
        <div class="caption">
          {{ loading ? "Loading guide..." : "No guide loaded" }}
        </div>
      </div>
      <a class="prev" @click="prevStep">❮</a>
      <a class="next" @click="nextStep">❯</a>
    </div>
  </div>
</template>

<script>
import { guideMixin } from "@/components/Guide/guideMixin";

export default {
  mixins: [guideMixin],
  props: {
    guide: {
      required: true,
    },
    fetchOnInit: {
      type: Boolean,
      default: true,
    },
    animate: {
      type: Boolean,
      default: false,
    },
    interval: {
      type: [Number, String],
      default: 5,
    },
  },
  data: () => ({
    stepImages: [],
    stepCaptions: [],
    currentStep: 1,
    playInterval: null,
    loading: false,
  }),
  mounted() {
    if (this.fetchOnInit) {
      this.fetchSteps();
    }
  },
  methods: {
    clearSteps() {
      this.stepImages = [];
      this.stepCaptions = [];
      this.currentStep = 1;
    },
    async fetchSteps() {
      this.clearSteps();
      if (!this.guide) return;
      try {
        this.loading = true;
        let images = [];
        let captions = [];
        let page = 1;
        while (page) {
          const response = await fetch(
            `${this.fetchBaseUrl}/steps?key=${this.fetchKey}&topic_id=${this.guide.id}&page=${page}`
          ).then((response) => response.json());
          const steps = response.data || [];
          steps.forEach((v) => {
            images.push(v.attributes.path);
            captions.push(v.attributes.name);
          });
          // Continue if not yet the last page
          if (
            response.meta &&
            response.meta.pagination &&
            response.meta.pagination.total_pages &&
            page < response.meta.pagination.total_pages
          ) {
            page++;
            continue;
          }
          break;
        }
        this.stepImages = images;
        this.stepCaptions = captions;
        this.$nextTick(() => {
          this.showStep(1);
        });
        this.$emit("fetch");
      } catch (err) {
        throw err;
      } finally {
        this.loading = false;
      }
    },
    nextStep() {
      let n = this.currentStep + 1;
      let next = n > this.stepImages.length ? 1 : n;
      this.showStep(next);
    },
    prevStep() {
      let n = this.currentStep - 1;
      let prev = n < 1 ? this.stepImages.length : n;
      this.showStep(prev);
    },
    showStep(n) {
      if (this.$refs.steps && this.$refs.steps[n - 1]) {
        this.$refs.steps.forEach((v) => {
          v.style.display = "none";
          v.style.visibility = "visible";
        });
        this.$refs.steps[n - 1].style.display = "block";
        // Preload prev step
        let prev = n - 1;
        prev = prev < 1 ? this.stepImages.length : prev;
        if (this.$refs.steps[prev - 1]) {
          this.$refs.steps[prev - 1].style.display = "unset";
          this.$refs.steps[prev - 1].style.visibility = "hidden";
        }
        // Preload next step
        let next = n + 1;
        next = next > this.stepImages.length ? 1 : next;
        if (this.$refs.steps[next - 1]) {
          this.$refs.steps[next - 1].style.display = "unset";
          this.$refs.steps[next - 1].style.visibility = "hidden";
        }
        this.currentStep = n;
      }
    },
    play() {
      const interval = parseFloat(this.interval);
      if (interval) {
        this.playInterval = setInterval(() => {
          this.nextStep();
        }, interval * 1000);
        this.$emit("play");
      }
    },
    pause() {
      clearInterval(this.playInterval);
      this.$emit("pause");
    },
  },
};
</script>

<style lang="scss" scoped>
.guide-slider {
  height: 735px;

  .steps-container {
    height: inherit;
    position: relative;
    max-width: 1070px;
    margin: auto;
    border-radius: 6px;
    border: 1px solid #eee;
  }

  .steps {
    display: none;
    height: inherit;
  }

  .image {
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    background-size: 100%;
    background-position: 0;
  }

  .prev,
  .next {
    cursor: pointer;
    position: absolute;
    top: 50%;
    width: auto;
    padding: 16px;
    margin-top: -22px;
    color: white;
    font-weight: bold;
    font-size: 18px;
    border-radius: 0 3px 3px 0;
    user-select: none;
    background-color: rgba(0, 0, 0, 0.8);
  }

  .next {
    right: 0;
    border-radius: 3px 0 0 3px;
  }

  .page {
    color: #eee;
    font-size: 12px;
    padding: 8px 12px;
    position: absolute;
    top: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }

  .title {
    color: #eee;
    font-size: 12px;
    padding: 8px 12px;
    position: absolute;
    top: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }

  .caption {
    color: #eee;
    font-size: 15px;
    padding: 8px 12px;
    position: absolute;
    bottom: 0;
    width: 100%;
    text-align: center;
    background-color: rgba(0, 0, 0, 0.5);
  }

  /* Fading animation */
  .fade {
    animation-name: fade;
    animation-duration: 1.5s;
  }

  @keyframes fade {
    from {
      opacity: 0.4;
    }
    to {
      opacity: 1;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Guide/GuideSlider.vue